import React, { Component } from "react";
import assign from "lodash/assign";
import hoistNonReactStatic from "hoist-non-react-statics";

import { getReactDisplayName, windowIsVisible } from "../Utils";

import ChatWebAPI from "./ChatWebAPI";

/**
 * Higher-Order component to add hearbeat functionality to a React component.
 *
 * @param {string} heartbeat url
 * @param {Object} heartbeat config
 * @param {React.Component} React component to be wrapped
 * @returns {React.Component} Wrapped React component
 *
 * @example
 *  const heartbeatConfig = {
 *      timer: 10000, // default is 5 seconds
 *      onSuccess: someFluxActions.onSuccess,
 *      onError: someFluxActions.onError,
 *  }
 *
 *  class MyComponent extends React.Component {
 *      componentDidMount() {
 *          this.props.heartbeat.start();
 *      }
 *
 *      componentWillUnmount() {
 *          this.props.heartbeat.stop();
 *      }
 *
 *      render() {
 *          ...
 *      }
 *  }
 *
 *  EnhancedComponent = chatHeartbeat("/heartbeat/load", heartbeatConfig)(MyComponent);
 */

const chatHeartbeat = (url, config) => WrappedComponent => {
    const defaults = {
        timer: 5000,
        pausedTimer: 20000,
        maxErrors: 3,
        onAuthFail: () => {},
        onError: () => {},
        onLoad: () => {},
        onSuccess: () => {},
    };

    class Heartbeat extends Component {
        constructor() {
            super();

            this.config = assign({}, defaults, config);
            this.errorCount = 0;
            this.isBusy = false;
            this.queryParams = {};
            this.timerId = undefined;
            this.heartbeatCount = 0;

            this.clearHeartbeatInterval = this.clearHeartbeatInterval.bind(this);
            this.getPublicMethods = this.getPublicMethods.bind(this);
            this.onVisibilityChange = this.onVisibilityChange.bind(this);
            this.onWebAPIRequestEnd = this.onWebAPIRequestEnd.bind(this);
            this.setQueryParams = this.setQueryParams.bind(this);
            this.startHeartbeat = this.startHeartbeat.bind(this);
            this.startHeartbeatInterval = this.startHeartbeatInterval.bind(this);
            this.stopHeartbeat = this.stopHeartbeat.bind(this);
        }

        componentDidMount() {
            document.addEventListener("visibilitychange", this.onVisibilityChange);
            ChatWebAPI.eventEmitter.on("request-start", this.onWebAPIRequestEnd);
        }

        componentWillUnmount() {
            document.removeEventListener("visibilitychange", this.onVisibilityChange);
            ChatWebAPI.eventEmitter.off("request-start", this.onWebAPIRequestEnd);
        }

        onVisibilityChange() {
            const heartbeatHasBeenInitialised = this.heartbeatCount > 0;

            if (windowIsVisible() && heartbeatHasBeenInitialised) {
                this.startHeartbeat();
            }
        }

        onWebAPIRequestEnd({ request }) {
            if (!request.isHeartbeat && request.shouldDeferHeartbeat) {
                this.startHeartbeatInterval();
            }
        }

        getPublicMethods() {
            return {
                start: this.startHeartbeat,
                stop: this.stopHeartbeat,
                query: this.setQueryParams,
            };
        }

        setQueryParams(params) {
            this.queryParams = params;
        }

        startHeartbeat() {
            const { maxErrors, onAuthFail, onError, onSuccess, pausedTimer } = this.config;

            this.startHeartbeatInterval();
            if (!this.isBusy && windowIsVisible()) {
                this.isBusy = true;
                ChatWebAPI.get(url, { isHeartbeat: true })
                    .query(this.queryParams)
                    .end((error, response) => {
                        this.isBusy = false;
                        if (error) {
                            if (error.status === 403 || error.status === 401) {
                                console.log("Authentication failed - startHeartbeat");
                                this.stopHeartbeat();
                                onAuthFail.call(undefined, response);
                            } else {
                                this.errorCount += 1;
                                if (this.errorCount >= maxErrors) {
                                    this.startHeartbeatInterval(pausedTimer);
                                }
                                onError.call(undefined, error);
                            }
                        } else {
                            if (this.errorCount > 0) {
                                this.startHeartbeatInterval();
                            }
                            this.errorCount = 0;
                            onSuccess.call(undefined, response.body);
                        }
                    });
            } else {
                console.log("my heart skipped a beat"); // eslint-disable-line no-console
            }
        }

        stopHeartbeat() {
            this.clearHeartbeatInterval();
            ChatWebAPI.abortAllRequests();
            this.isBusy = false;
            this.heartbeatCount = 0;
        }

        startHeartbeatInterval(milliseconds = this.config.timer) {
            window.clearTimeout(this.timerId);
            this.timerId = window.setTimeout(this.startHeartbeat, milliseconds);
            this.heartbeatCount += 1;
        }

        clearHeartbeatInterval() {
            window.clearTimeout(this.timerId);
            this.timerId = undefined;
        }

        render() {
            return <WrappedComponent heartbeat={this.getPublicMethods()} {...this.props} {...this.state} />;
        }
    }

    Heartbeat.displayName = `Heartbeat::${getReactDisplayName(WrappedComponent)}`;
    hoistNonReactStatic(Heartbeat, WrappedComponent);

    return Heartbeat;
};

export default chatHeartbeat;
